// This is for reducers that have both UI and data effects
// All components call this file when selecting data

import { combineReducers } from "redux";
import data, * as dataSelectors from "./data";
import ui, * as uiSelectors from "./ui";
import pointInPolygon from "@turf/boolean-point-in-polygon";
import turfPoint from "turf-point";
import { centerFromProject } from "../modules/project";
import { createSelector } from "reselect";
import bboxPolygon from "@turf/bbox-polygon";
import orderBy from "lodash/orderBy";

export default combineReducers({ data, ui });

//Data
export const selectGeoJSON = (s) => dataSelectors.selectGeoJSON(s.data);
export const selectAllProjects = (s) => dataSelectors.selectAllProjects(s.data);
export const selectAllRegionContent = (s) =>
  dataSelectors.selectAllRegionContent(s.data);

//UI
export const selectActiveFeatureName = (s) =>
  uiSelectors.selectActiveFeatureName(s.ui);
export const selectClickedFeature = (s) =>
  uiSelectors.selectClickedFeature(s.ui);
export const selectHoveredFeature = (s) =>
  uiSelectors.selectHoveredFeature(s.ui);
export const selectSearchBarVisible = (s) =>
  uiSelectors.selectSearchBarVisible(s.ui);
export const selectSearchedLocation = (s) =>
  uiSelectors.selectSearchedLocation(s.ui);
export const selectActiveProject = (s) => uiSelectors.selectActiveProject(s.ui);
export const selectBasemap = (s) => uiSelectors.selectBasemap(s.ui);
export const selectActivePage = (s) => uiSelectors.selectActivePage(s.ui);
export const selectViewingHub = (s) => uiSelectors.selectViewingHub(s.ui);
export const selectMapType = (s) => uiSelectors.selectMapType(s.ui);
export const selectIsInternal = (s) => uiSelectors.selectIsInternal(s.ui);
export const selectZoomToNsw = (s) => uiSelectors.selectZoomToNsw(s.ui);
export const selectSidebarIsOpen = (s) => uiSelectors.selectSidebarIsOpen(s.ui);
export const selectSidebarScrollup = (s) =>
  uiSelectors.selectSidebarScrollup(s.ui);

// Combined selectors
export const selectActiveFeature = createSelector(
  selectActiveFeatureName,
  selectGeoJSON,
  (featureName, geojson) => {
    if (!geojson) return undefined;
    return geojson.features.find((x) => {
      return x.properties["Functional"] === featureName;
    });
  }
);

export const selectActiveProjects = (s) => {
  const activeProject = selectActiveProject(s);
  if (!activeProject) return [];
  const projects = selectAllProjects(s);

  return projects.filter((x) => {
    return (
      parseFloat(x.latitude) === parseFloat(activeProject.latitude) &&
      parseFloat(x.longitude) === parseFloat(activeProject.longitude)
    );
  });
};

export const selectActiveFilteredProjects = createSelector(
  selectAllProjects,
  selectActiveProjects,
  (projects, activeProjects) => {
    const activeProjectsByName = activeProjects.reduce((acc, project) => {
      acc[project.project] = true;
      return acc;
    }, {});

    return projects.filter((x) => {
      return activeProjectsByName[x.project];
    });
  }
);
export const selectNonActiveFilteredProjects = createSelector(
  selectAllProjects,
  selectActiveProjects,
  (projects, activeProjects) => {
    const activeProjectsByName = activeProjects.reduce((acc, project) => {
      acc[project.project] = true;
      return acc;
    }, {});

    return projects.filter((x) => {
      return !activeProjectsByName[x.project];
    });
  }
);

// For displaying projects in sidebar that are within the selected region
// And are within the selected filters
export const selectAllProjectsInActiveFeature = createSelector(
  selectActiveFeature,
  selectActiveProject,
  selectAllProjects,
  (activeFeature, activeProject, projects) => {
    let filteredProjects;

    if (!activeFeature) {
      // This is the case when the sidebar is force-opened before selecting anything
      filteredProjects = projects;
    } else {
      const featureBboxPoly = bboxPolygon(activeFeature.properties.bbox);
      filteredProjects = projects.filter((p) => {
        const point = turfPoint(centerFromProject(p));
        if (!pointInPolygon(point, featureBboxPoly)) {
          return false;
        }
        return pointInPolygon(point, activeFeature);
      });
    }

    let orderedProjects = orderBy(filteredProjects, ["project"], ["asc"]);
    if (activeProject) {
      orderedProjects = orderBy(
        filteredProjects,
        (project) => {
          return project.project === activeProject.project;
        },
        ["desc"]
      );
    }

    return orderedProjects;
  }
);



// WEBPACK FOOTER //
// ./src/reducers/index.js